import {
  Box,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Icon,
  Skeleton,
  Switch,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { MdNotifications } from 'react-icons/md';
import Card from 'components/card/Card';
import { useNotificationsConfig, useSetNotificationsEnabled } from 'hooks/useNotificationsConfig';

export default function NotificationsConfigCard(props: { [x: string]: any }) {
  const { ...rest } = props;
  const toast = useToast();
  const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor       = useColorModeValue('gray.500', 'gray.400');
  const toggleBg         = useColorModeValue('gray.50', 'whiteAlpha.50');

  const { data: config, isLoading } = useNotificationsConfig();
  const setEnabled = useSetNotificationsEnabled();

  const handleToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.checked;
    try {
      await setEnabled.mutateAsync(next);
      toast({
        title: next ? 'Notificaciones activadas' : 'Notificaciones desactivadas',
        description: next
          ? 'El sistema enviará alertas automáticas a pacientes y profesionales.'
          : 'No se enviarán mensajes automáticos hasta que se reactiven.',
        status: next ? 'success' : 'warning',
        duration: 3500,
        isClosable: true,
        position: 'top-right',
      });
    } catch {
      toast({
        title: 'Error al cambiar configuración',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  return (
    <Card {...rest}>
      {/* Header */}
      <Flex align='center' gap='12px' mb='20px'>
        <Flex w='36px' h='36px' bg='brand.500' borderRadius='10px'
          align='center' justify='center' flexShrink={0}>
          <Icon as={MdNotifications} color='white' w='18px' h='18px' />
        </Flex>
        <Box>
          <Text color={textColorPrimary} fontWeight='bold' fontSize='lg' mb='1px'>
            Notificaciones Automáticas
          </Text>
          <Text color={mutedColor} fontSize='sm'>
            Alertas de citas por WhatsApp/Email a pacientes y profesionales.
          </Text>
        </Box>
      </Flex>

      {/* Toggle */}
      {isLoading ? (
        <Skeleton h='40px' borderRadius='8px' />
      ) : (
        <FormControl display='flex' alignItems='center' justifyContent='space-between'
          bg={toggleBg}
          borderRadius='12px' p='14px 16px'>
          <Box>
            <FormLabel htmlFor='notif-toggle' mb='0' cursor='pointer'
              color={textColorPrimary} fontWeight='600' fontSize='sm'>
              Activar Notificaciones Automáticas
            </FormLabel>
            <FormHelperText mt='2px' fontSize='xs' color={mutedColor}>
              {config?.isEnabled
                ? 'Activo — el sistema enviará mensajes al agendar o reagendar citas.'
                : 'Inactivo — ningún mensaje será enviado al exterior.'}
            </FormHelperText>
          </Box>
          <Switch
            id='notif-toggle'
            colorScheme='brand'
            size='lg'
            isChecked={config?.isEnabled ?? false}
            onChange={handleToggle}
            isDisabled={setEnabled.isPending}
          />
        </FormControl>
      )}
    </Card>
  );
}
